/* Numeri — recherche prédictive multi-ressources */
(function () {
  'use strict';

  class BdrSearchDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-bdr-search-results]');
      this.initial = this.querySelector('[data-bdr-search-initial]');
      this.status = this.querySelector('[data-bdr-search-status]');
      this.closeButton = this.querySelector('[data-bdr-search-close]');
      this.endpoint = this.dataset.bdrEndpoint || '/search/suggest';
      this.limit = Math.max(1, Number.parseInt(this.dataset.bdrLimit, 10) || 5);
      this.showImages = this.dataset.bdrShowImages === 'true';
      this.showVendor = this.dataset.bdrShowVendor === 'true';
      this.timer = null;
      this.controller = null;

      if (!this.dialog || !this.input || !this.results || typeof this.dialog.showModal !== 'function') return;

      this.onDocumentClick = (event) => {
        const trigger = event.target.closest('[data-bdr-search-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.open();
      };
      this.onClose = () => this.dialog.close();
      this.onBackdrop = (event) => {
        if (event.target === this.dialog) this.dialog.close();
      };
      this.onInput = () => this.scheduleSearch();
      this.onDialogClose = () => this.abortSearch();

      document.addEventListener('click', this.onDocumentClick);
      this.closeButton?.addEventListener('click', this.onClose);
      this.dialog.addEventListener('click', this.onBackdrop);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.input.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.closeButton?.removeEventListener('click', this.onClose);
      this.dialog?.removeEventListener('click', this.onBackdrop);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.input?.removeEventListener('input', this.onInput);
      this.abortSearch();
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      window.requestAnimationFrame(() => this.input.focus());
    }

    scheduleSearch() {
      window.clearTimeout(this.timer);
      const query = this.input.value.trim();
      if (!query) {
        this.abortSearch();
        this.showInitial();
        return;
      }
      this.timer = window.setTimeout(() => this.search(query), 250);
    }

    abortSearch() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    async search(query) {
      this.abortSearch();
      this.controller = new AbortController();
      const endpoint = this.endpoint.endsWith('.json') ? this.endpoint : `${this.endpoint}.json`;
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', String(this.limit));
      url.searchParams.set('resources[options][unavailable_products]', 'hide');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search ${response.status}`);
        const payload = await response.json();
        if (this.input.value.trim() !== query) return;
        this.render(payload?.resources?.results || {});
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderMessage(this.dataset.bdrError || 'Search unavailable.');
      }
    }

    render(groups) {
      const definitions = [
        ['products', this.dataset.bdrProductsLabel || 'Products'],
        ['collections', this.dataset.bdrCollectionsLabel || 'Collections'],
        ['pages', this.dataset.bdrPagesLabel || 'Pages'],
        ['articles', this.dataset.bdrArticlesLabel || 'Articles'],
      ];
      const fragment = document.createDocumentFragment();
      let count = 0;

      definitions.forEach(([key, label]) => {
        const items = Array.isArray(groups[key]) ? groups[key] : [];
        if (!items.length) return;
        const section = document.createElement('section');
        section.className = 'bdr-search-drawer__group';
        const heading = document.createElement('h2');
        heading.className = 'bdr-search-drawer__group-title';
        heading.textContent = label;
        const list = document.createElement('ul');
        list.className = 'bdr-search-drawer__group-list';
        list.setAttribute('role', 'list');
        items.forEach((item) => list.appendChild(this.createResult(item, key)));
        section.append(heading, list);
        fragment.appendChild(section);
        count += items.length;
      });

      this.results.replaceChildren();
      if (!count) {
        this.renderMessage(this.dataset.bdrNoResults || 'No results found.');
        return;
      }
      this.results.appendChild(fragment);
      this.showResults();
      this.status.textContent = (this.dataset.bdrResultsCount || '[count] results found.').replace('[count]', String(count));
    }

    createResult(item, type) {
      const listItem = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'bdr-search-drawer__result-link';
      link.href = item.url || '#';

      const imageUrl = item.featured_image?.url || item.image?.url;
      if (type === 'products' && this.showImages && imageUrl) {
        const image = document.createElement('img');
        image.className = 'bdr-search-drawer__thumb';
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.title || '';
        image.loading = 'lazy';
        image.width = 128;
        image.height = 128;
        link.appendChild(image);
      }

      const copy = document.createElement('span');
      copy.className = 'bdr-search-drawer__result-copy';
      const title = document.createElement('span');
      title.className = 'bdr-search-drawer__result-title';
      title.textContent = item.title || '';
      copy.appendChild(title);

      if (type === 'products' && this.showVendor && item.vendor) {
        const meta = document.createElement('span');
        meta.className = 'bdr-search-drawer__result-meta';
        meta.textContent = item.vendor;
        copy.appendChild(meta);
      }

      link.appendChild(copy);
      listItem.appendChild(link);
      return listItem;
    }

    renderMessage(message) {
      const paragraph = document.createElement('p');
      paragraph.className = 'bdr-search-drawer__message';
      paragraph.textContent = message;
      this.results.replaceChildren(paragraph);
      this.showResults();
      this.status.textContent = message;
    }

    showResults() {
      this.initial.hidden = true;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    showInitial() {
      this.results.hidden = true;
      this.results.replaceChildren();
      this.initial.hidden = false;
      this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = '';
    }
  }

  if (!customElements.get('bdr-search-drawer')) {
    customElements.define('bdr-search-drawer', BdrSearchDrawer);
  }
})();
